/**
 * A mobile scan's coverage, read from its preprocess step directory: the `area` table of
 * `coverage.gpkg` and the survey figures of `result.json` (forun's MLS preprocess). Both are
 * small, so they are fetched whole on every page load.
 */
import { useEffect, useState } from 'react';
import type { FeatureCollection } from 'geojson';
import type { LinkSource } from '@/map/linkSource';
import { SCAN_AREA_ID } from '@/map/layers';
import { loadGpkgLayer } from '@/map/vectorSource';

export const COVERAGE_FILE = 'coverage.gpkg';
export const RESULT_FILE = 'result.json';
const AREA_TABLE = 'area';

/** The per-area plots forun draws, in the order the page shows them. */
export const AREA_PLOTS = ['height', 'density', 'stem_zone_density', 'ground_vs_dtm'] as const;
export type AreaPlot = (typeof AREA_PLOTS)[number];
export const plotPath = (areaId: number, plot: AreaPlot) => `plots/area_${areaId}/${plot}.png`;

export interface ScanArea {
  id: number;
  areaHa: number | null;
  goodShare: number | null;
  pointsIn: number | null;
  pointsOut: number | null;
  densityPerHa: number | null;
  dtmMedianM: number | null;
  dtmMadM: number | null;
  dtmTiltPer100M: number | null;
}

export const TIERS = ['extent', 'edge', 'good', 'core'] as const;
export type Tier = (typeof TIERS)[number];

export interface ScanSummary {
  points: number | null;
  files: number | null;
  /** Hectares each coverage tier spans, and the stem-zone density that defines it. */
  coverage: Partial<Record<Tier, { areaHa: number; minPointsM2: number | null }>>;
  dtm: { medianM: number; madM: number | null } | null;
}

const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const isObject = (v: unknown): v is Record<string, unknown> => v !== null && typeof v === 'object' && !Array.isArray(v);

export function scanAreas(fc: FeatureCollection): ScanArea[] {
  return fc.features
    .map((f) => f.properties ?? {})
    .filter((p) => num(p[SCAN_AREA_ID]) !== null)
    .map((p) => ({
      id: num(p[SCAN_AREA_ID])!,
      areaHa: num(p.area_ha),
      goodShare: num(p.good_share),
      pointsIn: num(p.points_in),
      pointsOut: num(p.points_out),
      densityPerHa: num(p.density_out_per_ha),
      dtmMedianM: num(p.dtm_median_m),
      dtmMadM: num(p.dtm_mad_m),
      dtmTiltPer100M: num(p.dtm_tilt_m_per_100m),
    }))
    .sort((a, b) => a.id - b.id);
}

/** Read leniently: `result.json` is forun's free-form result, these keys are conventions. */
export function scanSummary(raw: unknown): ScanSummary {
  const r = isObject(raw) ? raw : {};
  const coverage: ScanSummary['coverage'] = {};
  const tiers = isObject(r.coverage) ? r.coverage : {};
  for (const tier of TIERS) {
    const v = tiers[tier];
    const areaHa = isObject(v) ? num(v.area_ha) : null;
    if (isObject(v) && areaHa !== null) coverage[tier] = { areaHa, minPointsM2: num(v.min_points_m2) };
  }
  const dtm = isObject(r.dtm) && num(r.dtm.median_m) !== null ? { medianM: num(r.dtm.median_m)!, madM: num(r.dtm.mad_m) } : null;
  return { points: num(r.points), files: num(r.file_count), coverage, dtm };
}

export interface ScanData {
  areas: FeatureCollection | null;
  summary: ScanSummary | null;
  loading: boolean;
  /** The areas could not be read; the summary is optional and its failure only leaves it out. */
  failed: boolean;
}

export function useScanData(object: string | undefined, links: LinkSource): ScanData {
  const [data, setData] = useState<ScanData>({ areas: null, summary: null, loading: true, failed: false });
  useEffect(() => {
    if (!object) return;
    let alive = true;
    const areas = links.url(object, COVERAGE_FILE).then((url) => loadGpkgLayer(url, { table: AREA_TABLE }));
    const summary = links
      .url(object, RESULT_FILE)
      .then((url) => fetch(url))
      .then((r) => (r.ok ? r.json() : null))
      .then(scanSummary)
      .catch(() => null);
    void Promise.allSettled([areas, summary]).then(([a, s]) => {
      if (!alive) return;
      setData({
        areas: a.status === 'fulfilled' ? a.value : null,
        summary: s.status === 'fulfilled' ? s.value : null,
        loading: false,
        failed: a.status === 'rejected',
      });
    });
    return () => {
      alive = false;
    };
  }, [object, links]);
  return data;
}
